import styled from 'styled-components';

import style from '../../../style';
import type Theme from '../../../style/types/theme';
import { BorderRadiusSize, Variant } from './types';
import { Wrapper as InvisibleInputWrapper } from './components/InvisibleInput/InvisibleInput.styled';
import { InvisibleText } from './components/InvisibleText/InvisibleText.styled';

interface InputChipWrapperProps {
  $variant: string;
  $height: string;
  $disabled?: boolean;
  $hasErrors?: boolean;
  $focused?: boolean;
  $borderRadiusSize?: BorderRadiusSize;
}

interface InputChipProps {
  $variant: string;
  $height: string;
  $focused?: boolean;
}

const getCursorStyle = (disabled: boolean, focused?: boolean) => {
  if (disabled) {
    return 'not-allowed';
  }

  if (focused) {
    return 'text';
  }

  return 'auto';
};

const getBorderRadiusSize = (
  size: Theme['size'],
  borderRadiusSize?: BorderRadiusSize,
) => {
  switch (borderRadiusSize) {
    case BorderRadiusSize.SMALL:
      return size.units(0.5);
    case BorderRadiusSize.MEDIUM:
      return size.units(1);
    case BorderRadiusSize.BIG:
      return size.units(2);
    case BorderRadiusSize.NONE:
    default:
      return 0;
  }
};

const getBorderColor = (
  focused: boolean,
  disabled: boolean,
  hasErrors: boolean,
  color: Theme['color'],
) => {
  if (disabled) {
    return `1px solid ${color.border.primary.disabled()}`;
  }

  if (hasErrors) {
    return `1px solid ${color.border.error.default()}`;
  }

  if (focused) {
    return `1px solid ${color.border.accent.default()}`;
  }

  return `1px solid ${color.border.primary.default()}`;
};

const isExpansible = ($variant: string) => $variant === Variant.EXPANSIBLE;

const shouldCollapse = (isExpansible: boolean, $focused: boolean): boolean =>
  isExpansible && !$focused;

const getMinHeight = ({
  $height,
  $variant,
  size,
}: {
  $height: string;
  $variant: string;
  size: Theme['size'];
}) => {
  if ($variant === Variant.FIXED) {
    return $height;
  }

  return size.units(5);
};

const getHeight = ({ $height, $variant }: { $height: string; $variant: string }) => {
  if ($variant === Variant.FIXED) {
    return $height;
  }

  return 'auto';
};

export const Wrapper = styled.div({
  position: 'relative',
  width: '100%',

  [InvisibleInputWrapper]: {
    float: 'left',
  },

  [InvisibleText]: {
    position: 'absolute',
  },
});

export const InputChipWrapper = styled.div<InputChipWrapperProps>(
  ({
    $borderRadiusSize,
    $disabled = false,
    $focused = false,
    $hasErrors = false,
    $height,
    $variant,
    theme: { transition, size, color },
  }) => ({
    width: '100%',
    minHeight: getMinHeight({ $variant, $height, size }),
    height: getHeight({ $height, $variant }),
    boxSizing: 'border-box',
    cursor: getCursorStyle($disabled, $focused),
    borderRadius: getBorderRadiusSize(size, $borderRadiusSize),
    transition: transition.standard(),
    border: getBorderColor($focused, $disabled, $hasErrors, color),
    overflowX: shouldCollapse(isExpansible($variant), $focused) ? 'auto' : 'hidden',
    overflowY: 'auto',
    padding: `0 ${size.units(1)} 0 ${size.units(1.5)}`,
    backgroundColor: $disabled
      ? color.background.primary.disabled()
      : color.background.primary.default(),

    ...style.mixins.hideScrollbar(),

    ':focus-visible': {
      outline: 'auto -webkit-focus-ring-color',
    },
  }),
);

export const StyledInputChip = styled.div<InputChipProps>(
  ({ theme, theme: { size }, $variant, $focused = false, $height }) => {
    return {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      alignContent: 'flex-start',
      maxHeight: $height,
      overflow: shouldCollapse(isExpansible($variant), $focused) ? 'visible' : 'auto',
      flexWrap: shouldCollapse(isExpansible($variant), $focused) ? 'nowrap' : 'wrap',
      gap: size.units(1),
      padding: `${size.units(1)} ${size.units(1)} ${size.units(1)} 0`,

      ...style.mixins.scrollbar(theme),

      '&::-webkit-scrollbar-track': {
        margin: `${size.units(1)} 0 ${size.units(1)} 0`,
      },
    };
  },
);

export const InputChipContainer = styled.div(() => {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
  };
});

export const ListWrapper = styled.div(({ theme: { size } }) => ({
  maxHeight: size.units(24),
}));

interface StyledSuggestionComponentProps {
  $isLocked?: boolean;
  $isSelected?: boolean;
  theme: Theme;
}

export const SuggestionComponentWrapper = styled.div<StyledSuggestionComponentProps>(
  ({ theme: { color }, $isLocked, $isSelected }) => {
    return {
      cursor: $isLocked ? 'default' : 'pointer',
      color: $isLocked
        ? color.content.primary.disabled()
        : color.content.primary.default(),
      backgroundColor: $isSelected ? color.background.primary.active() : 'inherit',
      '&:hover': {
        backgroundColor: $isLocked ? 'inherit' : color.background.primary.hover(),
      },
    };
  },
);
